import React, { useState } from 'react';
import { X, Plus, Loader2 } from 'lucide-react';
import { workCenterService } from '../../services/workCenterService';

// =============================================================================
// Add work centre — shared by Work Centre Costing and Manufacturing Setup.
//
// A work centre is the master record both pages hang off: Costing gives it two
// rates and its routing times, Setup gives it its posting accounts. Neither
// page can create one on its own, so this dialog lives with both.
//
// Writes through the existing /api/work-centers master upsert.
// =============================================================================

// Industry-neutral suggestions — the driver is whatever unit of capacity this
// centre sells, so never hard-code "hour" as the only option.
const DRIVER_SUGGESTIONS = ['machine-hour', 'labour-hour', 'chair-hour', 'drop', 'km', 'batch'];

const AddWorkCentreModal = ({ existingCodes = [], onAdded, onClose }) => {
  const [code, setCode] = useState('');
  const [description, setDescription] = useState('');
  const [driver, setDriver] = useState('machine-hour');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const trimmed = code.trim().toUpperCase();
  const duplicate = existingCodes.some((c) => c.toUpperCase() === trimmed);
  const problem = !trimmed
    ? ''
    : duplicate
      ? 'A work centre with this code already exists at this site.'
      : '';
  const canAdd = trimmed && description.trim() && !problem && !busy;

  const submit = async () => {
    if (!canAdd) return;
    setBusy(true);
    setError('');
    try {
      const res = await workCenterService.saveWorkCenters({
        centers: [{
          code: trimmed,
          description: description.trim(),
          driver_uom: driver.trim() || null,
          // Cost centre is no longer captured here — posting accounts live on
          // Manufacturing > Setup, split by setup and run.
          cost_center_code: null,
          notes: null,
        }],
        rates: [],
      });
      if (res?.success) onAdded(trimmed);
      else setError(res?.message || 'Could not add the work centre.');
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Could not add the work centre.');
    } finally {
      setBusy(false);
    }
  };

  const inputClass =
    'text-sm rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 ' +
    'text-slate-800 dark:text-slate-100 px-2.5 py-2 outline-none focus:ring-2 focus:ring-slate-400 disabled:opacity-50';

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-6"
      onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}
    >
      <div className="w-full max-w-md rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-2xl">
        <div className="px-5 py-4 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/95 flex items-start justify-between gap-3">
          <div>
            <h3 className="m-0 text-sm font-bold text-slate-800 dark:text-slate-100">Add work centre</h3>
            <p className="m-0 mt-0.5 text-xs text-slate-500 dark:text-slate-400">
              A resource centre that recovers cost through a capacity driver.
            </p>
          </div>
          <button
            type="button" onClick={onClose} disabled={busy} aria-label="Close"
            className="p-1.5 rounded-md text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-700 disabled:opacity-50"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 flex flex-col gap-3">
          <label className="flex flex-col gap-1 text-xs text-slate-500 dark:text-slate-400">
            Code
            <input
              type="text" className={`${inputClass} font-mono`} placeholder="e.g. FIL-02"
              value={code} disabled={busy} autoFocus
              onChange={(e) => setCode(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
            />
          </label>

          <label className="flex flex-col gap-1 text-xs text-slate-500 dark:text-slate-400">
            Description
            <input
              type="text" className={inputClass} placeholder="e.g. Filling Line 2"
              value={description} disabled={busy}
              onChange={(e) => setDescription(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
            />
          </label>

          <label className="flex flex-col gap-1 text-xs text-slate-500 dark:text-slate-400">
            Driver — the unit of capacity this centre sells
            <input
              type="text" list="wc-driver-suggestions" className={inputClass}
              value={driver} disabled={busy}
              onChange={(e) => setDriver(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
            />
            <datalist id="wc-driver-suggestions">
              {DRIVER_SUGGESTIONS.map((d) => <option key={d} value={d} />)}
            </datalist>
          </label>

          <p className="m-0 min-h-[1rem] text-xs text-red-600 dark:text-red-400">{problem || error || ''}</p>
          <p className="m-0 text-xs text-slate-400">
            Rates and routing times are set on the Costing tabs; cost centre and GL account on Manufacturing&nbsp;&gt;&nbsp;Setup.
          </p>

          <div className="mt-1 flex justify-end gap-2">
            <button
              type="button" onClick={onClose} disabled={busy}
              className="px-4 py-2 text-sm font-medium rounded-md border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button" onClick={submit} disabled={!canAdd}
              className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-md text-white transition-colors ${
                canAdd ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-slate-400 dark:bg-slate-600'
              }`}
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
              {busy ? 'Adding…' : 'Add work centre'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddWorkCentreModal;
